import {
	type PlacementMessageFrontendFactory,
	type DefaultMessagesResponse,
	type AnyProps,
	type NoProps,
	type PlacementRenderer,
	type AnyFrontendStage,
	type PlacementBrowserConfig,
} from '@post-office/message-maker-browser/types';

import { createPlacementFrontend } from '@post-office/message-maker-browser/create-placement-frontend';

import { useState, useEffect, useCallback } from 'react';

import { usePlacementApi } from '@post-office/use-placement-api';

type CustomDismissCallback = (messageInstanceId: string) => void;

export type placementRenderProps = {
	dismiss: () => void;
	onPrimaryAction?: (e: React.MouseEvent<HTMLButtonElement, Event>) => void;
	messageInstanceId: string;
};

const placementConfig: PlacementBrowserConfig = {
	placementId: `jira-issue-view-playbooks-panel`,
	options: {
		choreographer: {
			isDefaultEnabled: true, // You almost always will want this option to be true. See below for an explanation
		},
		hideInIsolatedCloud: false, // Set to true if your placement should not be shown in IC
	},
};

const placement = {
	createFrontend: createPlacementFrontend(placementConfig),
};

export const createFrontend: <U extends AnyProps = NoProps>(
	placementRenderer: PlacementRenderer<
		{
			render: AnyFrontendStage;
		},
		U
	>,
) => PlacementMessageFrontendFactory<
	{
		render: AnyFrontendStage;
	},
	U
> = placement.createFrontend;

export const PlacementFrontend: PlacementMessageFrontendFactory<
	{
		render: AnyFrontendStage;
	},
	{
		onPrimaryAction?: (e: React.MouseEvent<HTMLButtonElement, Event>) => void;
	}
> = createFrontend<{
	onPrimaryAction?: (e: React.MouseEvent<HTMLButtonElement, Event>) => void;
}>((Messages) => (placementProps) => {
	const [messages, setMessages] = useState<DefaultMessagesResponse['messages']>([]);

	const apiResponse = usePlacementApi<DefaultMessagesResponse>('/');

	const { error, data, isLoading } = apiResponse;

	useEffect(() => {
		if (isLoading) {
			return;
		}

		if (error) {
			throw error;
		}
		setMessages(data.messages);
	}, [apiResponse]);

	const handleDismiss: CustomDismissCallback = useCallback(
		(messageInstanceId) => {
			// On dismiss we remove the message from the local state using the messageInstanceId.
			setMessages(messages.filter((message) => messageInstanceId !== message.messageInstanceId));
		},
		[messages],
	);

	return (
		<>
			{!isLoading &&
				messages.map((props) => (
					<Messages.render
						key={props.messageInstanceId}
						{...props}
						dismiss={() => handleDismiss(props.messageInstanceId)}
						onPrimaryAction={placementProps.onPrimaryAction}
					/>
				))}
		</>
	);
});

export type PlacementFrontendProps = {
	onPrimaryAction?: (e: React.MouseEvent<HTMLButtonElement, Event>) => void;
};
